import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

// ngx-extended-pdf-viewer wraps pdf.js, which internally makes 200+ document.getElementById()
// (and similar) calls that search the whole page rather than being scoped to one viewer
// instance. Two <ngx-extended-pdf-viewer> instances mounted at the same time — e.g. a
// drawer-hosted viewer (Page Tips, the file preview drawer) opened over a page that has its
// own persistent viewer (a PDA/invoice/credit note export preview) — will silently swap or
// blank each other's content. This is a documented, unfixed limitation of the library; there
// is no supported way to isolate instances short of putting each one in a real <iframe>
// (see https://github.com/stephanrauh/ngx-extended-pdf-viewer/issues/190).
//
// Rather than truly isolating them (which would mean losing the shared custom toolbar for
// whichever viewer gets iframe-wrapped), this service lets drawer/modal-hosted viewers
// announce "I'm open" so persistent page-level viewers can temporarily unmount themselves
// while one is up, and remount once it closes. Every viewer still renders with its normal,
// unchanged UI whenever only one is on screen — which is the case for the overwhelming
// majority of usage.
@Injectable({ providedIn: 'root' })
export class PdfViewerCoordinatorService {
  private readonly activeCount$ = new BehaviorSubject<number>(0);
  readonly isModalViewerActive$ = this.activeCount$.pipe(map(count => count > 0));

  acquire(): void {
    this.activeCount$.next(this.activeCount$.value + 1);
  }

  release(): void {
    this.activeCount$.next(Math.max(0, this.activeCount$.value - 1));
  }
}
